import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { func, bool, string, arrayOf, number } from 'prop-types';

import { Icon } from '@atoms';
import { Flex, Button, Typography } from '@mixins';
import { theme } from '@styles';
import { formatHourValue, getHoursList } from '@helpers/time';
import { QUARTER_INTERVALS } from '@constants/common';

import { ReactComponent as Close } from '@assets/svgs/Close.svg';

import { HoveredBlock, ScrollableContainer } from '../styles';

const DetailedTimeSelectingModal = ({ onModalClose, canBeClosed, onSelect, selectedTime, topPosition }) => {
  const [hoursSelected, setHoursSelected] = useState(selectedTime.slice(0, 2));
  const [minutesSelected, setMinutesSelected] = useState(selectedTime.slice(3));

  const { currentLocation } = useSelector(store => store.locationStore);

  const hoursList = useMemo(
    () => getHoursList().map(hours => formatHourValue(hours, currentLocation.timeFormat)),
    [currentLocation],
  );

  const getSelectingBlock = useCallback(
    (items, selectedValue, selectHandler, formatter) =>
      items.map(item => {
        return (
          <HoveredBlock
            key={item}
            mb="6px"
            pointer
            justifyContent="center"
            alignItems="center"
            width="100%"
            onClick={selectHandler(item)}
            px={40}
            py="10px"
            isSelected={selectedValue === item}>
            <Typography variant="diatype-400" fontSize={16} color={theme.color.gray[300]}>
              {formatter ? formatter(item) : item}
            </Typography>
          </HoveredBlock>
        );
      }),
    [],
  );

  const handleHoursSelect = selectedValue => () => {
    setHoursSelected(selectedValue);
  };

  const handleMinutesSelect = selectedValue => () => {
    setMinutesSelected(selectedValue);
  };

  const handleModalClose = useCallback(() => {
    handleHoursSelect('');
    handleMinutesSelect('');
    onModalClose();
  }, []);

  const formatHours = useCallback(hour => formatHourValue(hour, currentLocation.timeFormat), [currentLocation]);

  const hoursBlock = useMemo(
    () => getSelectingBlock(hoursList, hoursSelected, handleHoursSelect, formatHours),
    [hoursList, hoursSelected, handleHoursSelect],
  );

  const minutesBlock = useMemo(
    () => getSelectingBlock(QUARTER_INTERVALS, minutesSelected, handleMinutesSelect),
    [minutesSelected, handleMinutesSelect],
  );

  return (
    <Flex
      zIndex={3}
      backgroundColor={theme.color.white}
      boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
      flexDirection="column"
      alignItems="center"
      position="absolute"
      top={topPosition}
      left={0}
      p={20}
      pt={canBeClosed ? 40 : 20}>
      {canBeClosed && (
        <Icon
          pointer
          onClick={handleModalClose}
          position="absolute"
          SVG={Close}
          width={11}
          height={11}
          top={17}
          right={17}
        />
      )}
      <Flex flexDirection="column">
        <Typography alignSelf="center" variant="rightserif-400" fontSize={22}>
          Arrival Time
        </Typography>
        <Typography
          width="100%"
          textAlign="center"
          mb={10}
          variant="diatype-400"
          fontSize={13}
          color={theme.color.gray[200]}>
          Open Hours: 9:00 - 17:00
        </Typography>
        <Flex>
          <Flex flexDirection="column">
            <Typography textAlign="center" variant="diatype-400" fontSize={14} color={theme.color.gray[200]}>
              Hours
            </Typography>
            <ScrollableContainer flexDirection="column" maxHeight={200} overflow="hidden">
              {hoursBlock}
            </ScrollableContainer>
          </Flex>
          <Flex flexDirection="column">
            <Typography textAlign="center" variant="diatype-400" fontSize={14} color={theme.color.gray[200]}>
              Minutes
            </Typography>
            <ScrollableContainer flexDirection="column" maxHeight={200} overflow="hidden">
              {minutesBlock}
            </ScrollableContainer>
          </Flex>
        </Flex>
      </Flex>
      <Button
        mt={15}
        onClick={onSelect(`${hoursSelected}:${minutesSelected}`)}
        py={12}
        width="100%"
        variant="primary"
        disabled={!hoursSelected || !minutesSelected}>
        Select
      </Button>
    </Flex>
  );
};

DetailedTimeSelectingModal.defaultProps = {
  topPosition: [0],
  canBeClosed: true,
  onModalClose: () => {},
};

DetailedTimeSelectingModal.propTypes = {
  topPosition: arrayOf(number),
  selectedTime: string.isRequired,
  onSelect: func.isRequired,
  onModalClose: func,
  canBeClosed: bool,
};

export default DetailedTimeSelectingModal;
